import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p className="text-ink-200">
        Durak (the "Service") is a free, online card game. This policy explains what
        information we store about you, why, and how to remove it. We try to keep this
        page short and accurate to what the code actually does.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account info</strong> — if you sign in with Google (or Facebook,
          where available) we store the provider's user ID, your display name,
          public profile photo URL, and primary email (only used to identify your
          account). If you sign in as a guest, we only store the display name you
          chose.
        </li>
        <li><strong>Gameplay history</strong> — game results, win/loss/draw counts, win streaks, and the games you participated in. This powers your stats page and the leaderboard.</li>
        <li><strong>Push subscription</strong> — only if you explicitly opt in. We store the browser-provided endpoint and keys so we can send game-invite notifications.</li>
        <li><strong>Session cookie</strong> — a signed, HTTP-only cookie that keeps you logged in. No tracking pixels or third-party analytics are loaded.</li>
      </ul>

      <h2>What we do not collect</h2>
      <ul>
        <li>No advertising IDs, no cross-site tracking, no third-party analytics.</li>
        <li>Your Facebook password is never seen by the Service — Facebook handles authentication and returns only the profile fields above.</li>
        <li>We do not read your Facebook friends list, messages, posts, or contacts.</li>
      </ul>

      <h2>How we use the data</h2>
      <ul>
        <li>To show your hand only to you and synchronise the shared game state in real time.</li>
        <li>To display stats (your own page) and a public leaderboard (display name + avatar + win counts).</li>
        <li>To send push notifications you've explicitly opted in to (game invites).</li>
      </ul>

      <h2>Sharing</h2>
      <p>
        We do not sell, rent, or share your personal data with third parties. Your display
        name, avatar, and aggregate win counts are visible to other players in the lobby and
        on the public leaderboard — that's the point of multiplayer.
      </p>

      <h2>Retention</h2>
      <p>
        Account and game-history rows are kept until you request deletion. Push subscriptions
        are deleted automatically when the browser invalidates them, or when you disable
        notifications in <Link to="/settings" className="text-accent underline">Settings</Link>.
        Abandoned in-memory game rooms are pruned after 6 hours of inactivity.
      </p>

      <h2 id="data-deletion">Deleting your data</h2>
      <p>
        To delete your account and all associated data, email
        {' '}<a className="text-accent underline" href="mailto:admin@durak.runnable.dev">admin@durak.runnable.dev</a>{' '}
        from the email registered on your Facebook account (or include your guest display
        name). We will remove your user row, game results, and push subscriptions within 30
        days and reply to confirm.
      </p>
      <p>
        You can also revoke this app's access at any time from your Facebook account
        settings under <em>Settings &amp; privacy → Apps and Websites</em>. When you remove
        the app from Facebook, Facebook calls our deletion webhook automatically — your
        account, game results, and push subscriptions are erased immediately and you receive
        a confirmation code with a status page like <code>/data-deletion/&lt;code&gt;</code>.
      </p>

      <h2>Cookies</h2>
      <p>
        We use a single first-party session cookie (HTTP-only, SameSite=Lax, Secure in
        production). It contains a signed session identifier — no personal data is stored
        inside the cookie itself.
      </p>

      <h2>Children</h2>
      <p>
        The Service is not directed to children under 13. If you believe a child has
        registered, please contact us and we will remove the account.
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy as the Service evolves. Material changes will be announced
        in-app the next time you sign in.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or requests? Reach us at{' '}
        <a className="text-accent underline" href="mailto:admin@durak.runnable.dev">admin@durak.runnable.dev</a>.
      </p>

      <p className="text-xs text-ink-300 mt-6">Last updated: 2025-05-23.</p>
    </LegalShell>
  );
}

export function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="min-h-full px-4 py-8"
      style={{
        paddingTop: 'calc(env(safe-area-inset-top) + 2rem)',
        paddingLeft: 'calc(env(safe-area-inset-left) + 1rem)',
        paddingRight: 'calc(env(safe-area-inset-right) + 1rem)',
      }}
    >
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-sm text-accent">← Back</Link>
        <h1 className="font-display text-3xl font-bold mt-4 mb-6">{title}</h1>
        <article className="panel p-6 space-y-3 prose-invert text-sm leading-relaxed
                            [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-1
                            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:text-ink-100
                            [&_p]:text-ink-100">
          {children}
        </article>
      </div>
    </div>
  );
}
